import { useEffect, useMemo, useState, useCallback } from "react";
import {
  useCreate,
  useInvalidate,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import { UsersIcon } from "@phosphor-icons/react";
import { DownOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  DatePicker,
  Flex,
  theme,
  Tree,
  ConfigProvider,
  Card,
  Segmented,
  notification,
} from "antd";
import dayjs from "dayjs";
import type { TreeProps } from "antd";
import { UserInviteResponse } from "types/";
import { USER_INVITE_EXPIRY_DAYS } from "@/constants";
import { useWorkspace } from "hooks/useWorkspace";
import { useUserRoles } from "hooks/useUserRoles";
import { radius } from "@libertify/ui";

interface UserInviteFormProps {
  organizationId: string;
  editingInvite: UserInviteResponse | null;
  onCancel: () => void;
  onSuccess: () => void;
}

type ExpiryPreset = "7" | "14" | "30" | "custom";

export const UserInviteForm = ({
  organizationId,
  editingInvite,
  onCancel,
  onSuccess,
}: UserInviteFormProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [api, contextHolder] = notification.useNotification();

  const {
    organizationRoleOptions,
    workspaceRoleOptions,
    isLoading: isLoadingRoles,
  } = useUserRoles();

  const [form] = Form.useForm();
  const invalidate = useInvalidate();
  const { workspaces } = useWorkspace({
    organizationId: organizationId,
  });

  const [expiryPreset, setExpiryPreset] = useState<ExpiryPreset>(() => {
    if (editingInvite) return "custom";
    if (USER_INVITE_EXPIRY_DAYS === 7) return "7";
    if (USER_INVITE_EXPIRY_DAYS === 14) return "14";
    if (USER_INVITE_EXPIRY_DAYS === 30) return "30";
    return "custom";
  });

  const [selectedWorkspaceRoles, setSelectedWorkspaceRoles] = useState<
    Record<string, string[]>
  >(() => {
    const roles: Record<string, string[]> = {};
    if (editingInvite?.workspace_roles) {
      Object.entries(editingInvite.workspace_roles).forEach(
        ([workspaceId, workspaceRoles]) => {
          roles[workspaceId] = workspaceRoles;
        }
      );
    }
    return roles;
  });
  const [workspaceTreeExpandedKeys, setWorkspaceTreeExpandedKeys] = useState<
    React.Key[]
  >(() => {
    if (editingInvite?.workspace_roles) {
      return Object.keys(editingInvite.workspace_roles);
    }
    return [];
  });
  const [shouldAutoExpand, setShouldAutoExpand] = useState(true);
  const [workspaceTreeAutoExpandParent, setWorkspaceTreeAutoExpandParent] =
    useState<boolean>(true);
  const [workspaceTreeCheckedKeys, setWorkspaceTreeCheckedKeys] = useState<
    React.Key[]
  >(() => {
    const checkedKeys: string[] = [];
    if (editingInvite?.workspace_roles) {
      Object.entries(editingInvite.workspace_roles).forEach(
        ([workspaceId, roles]) => {
          roles.forEach((role) => {
            if (role !== "Admin") {
              checkedKeys.push(`${workspaceId}-${role}`);
            }
          });
        }
      );
    }
    return checkedKeys;
  });
  const [treeKey] = useState<string>("tree-0");

  // Auto-expand all workspaces when there are 5 or fewer (one-time on mount)
  if (
    shouldAutoExpand &&
    workspaces &&
    workspaces.length > 0 &&
    workspaces.length <= 5 &&
    workspaceTreeExpandedKeys.length === 0 &&
    !editingInvite
  ) {
    setShouldAutoExpand(false);
    setWorkspaceTreeExpandedKeys(workspaces.map((w) => w.id));
  }

  const workspaceRolesTreeData = useMemo(() => {
    if (!workspaces || workspaces.length === 0) return [];

    return workspaces.map((workspace) => {
      const workspaceNode = {
        title: workspace.name,
        key: workspace.id,
        selectable: false,
        disabled: false,
        icon: (
          <UsersIcon
            size={16}
            weight="bold"
            aria-hidden="true"
            style={{ verticalAlign: "middle" }}
          />
        ),
        children: workspaceRoleOptions
          .filter((role) => role.label !== "Admin")
          .map((role) => ({
            title: role.label,
            key: `${workspace.id}-${role.value}`,
            isLeaf: true,
            selectable: false,
            disabled: false,
          })),
      };
      return workspaceNode;
    });
  }, [workspaces, workspaceRoleOptions]);

  const { mutateAsync: createInvite, isPending: isCreating } = useCreate();
  const { mutateAsync: updateInvite, isPending: isUpdating } = useUpdate();

  useEffect(() => {
    if (editingInvite) {
      form.setFieldsValue({
        recipient: editingInvite.recipient,
        organization_roles: editingInvite.organization_roles || [],
      });
    }
  }, [editingInvite, form]);

  const handleExpiryPresetChange = useCallback(
    (value: ExpiryPreset) => {
      setExpiryPreset(value);
      if (value !== "custom") {
        form.setFieldValue("expiry", dayjs().add(Number(value), "day"));
      }
    },
    [form]
  );

  const onTreeExpand = useCallback<TreeProps["onExpand"]>(
    (expandedKeysValue) => {
      setWorkspaceTreeExpandedKeys(expandedKeysValue);
      setWorkspaceTreeAutoExpandParent(false);
    },
    []
  );

  const onTreeCheck = useCallback<TreeProps["onCheck"]>(
    (checkedKeysValue) => {
      const newCheckedKeys = Array.isArray(checkedKeysValue)
        ? checkedKeysValue
        : checkedKeysValue.checked;

      setWorkspaceTreeCheckedKeys(newCheckedKeys);

      const newWorkspaceRoles: Record<string, string[]> = {};
      newCheckedKeys.forEach((key) => {
        const keyStr = key.toString();
        const isWorkspaceOnly = workspaces?.some(
          (workspace) => workspace.id === keyStr
        );
        if (isWorkspaceOnly) {
          return;
        }
        workspaces?.forEach((workspace) => {
          workspaceRoleOptions.forEach((role) => {
            const expectedKey = `${workspace.id}-${role.value}`;
            if (keyStr === expectedKey) {
              if (!newWorkspaceRoles[workspace.id]) {
                newWorkspaceRoles[workspace.id] = [];
              }
              if (!newWorkspaceRoles[workspace.id].includes(role.value)) {
                newWorkspaceRoles[workspace.id].push(role.value);
              }
            }
          });
        });
      });
      setSelectedWorkspaceRoles(newWorkspaceRoles);
      form.setFieldValue("workspace_roles_tree", newCheckedKeys);
    },
    [form, workspaceRoleOptions, workspaces]
  );

  const handleFinish = async (values: {
    recipient?: string;
    organization_roles?: string[];
    workspace_roles_tree?: React.Key[];
    expiry?: dayjs.Dayjs;
  }) => {
    if (!organizationId) {
      return;
    }

    const hasOrgRoles = values.organization_roles?.length > 0;
    const hasWorkspaceRoles = Object.keys(selectedWorkspaceRoles).length > 0;

    if (!hasOrgRoles && !hasWorkspaceRoles) {
      form.setFields([
        {
          name: "organization_roles",
          errors: [t("pages.userInvites.selectAtLeastOneRole")],
        },
      ]);
      return;
    }

    const workspaceRoles =
      Object.keys(selectedWorkspaceRoles).length > 0
        ? selectedWorkspaceRoles
        : {};

    const recipientEmail = values.recipient || editingInvite?.recipient;

    const success = async () => {
      onSuccess();
      await invalidate({
        resource: `api/${organizationId}/user-invites`,
        invalidates: ["list"],
      });
    };

    if (editingInvite) {
      const patchPayload = {
        workspace_roles: workspaceRoles,
        organization_roles: values.organization_roles || [],
        expiry: values.expiry ? dayjs(values.expiry).toISOString() : undefined,
      };

      await updateInvite({
        resource: `api/user-invites`,
        id: editingInvite.id,
        values: patchPayload,
      });
      api.success({
        message: t("pages.userInvites.inviteUpdatedSuccess"),
        description: t("pages.userInvites.inviteUpdatedDescription", {
          email: recipientEmail,
        }),
        placement: "bottomRight",
      });
      await success();
    } else {
      const postPayload = {
        recipient: values.recipient,
        workspace_roles: workspaceRoles,
        organization_roles: values.organization_roles || [],
        expiry: values.expiry ? dayjs(values.expiry).toISOString() : undefined,
      };

      await createInvite({
        resource: `api/${organizationId}/user-invites`,
        values: postPayload,
      });
      api.success({
        message: t("pages.userInvites.inviteSentSuccess"),
        description: t("pages.userInvites.inviteSentDescription", {
          email: recipientEmail,
        }),
        placement: "bottomRight",
      });
      await success();
    }
  };

  const buttonText =
    isCreating || isUpdating
      ? editingInvite
        ? t("pages.userInvites.updating")
        : t("pages.userInvites.sending")
      : editingInvite
        ? t("pages.userInvites.updateInvitation")
        : t("pages.userInvites.sendInvitation");

  const expiryPresetOptions = [
    { label: t("pages.userInvites.expiryPreset7"), value: "7" },
    { label: t("pages.userInvites.expiryPreset14"), value: "14" },
    { label: t("pages.userInvites.expiryPreset30"), value: "30" },
    { label: t("pages.userInvites.expiryPresetCustom"), value: "custom" },
  ];

  return (
    <Card
      title={
        editingInvite
          ? t("pages.userInvites.updateUserInvitation")
          : t("pages.userInvites.sendNewUserInvitation")
      }
    >
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          expiry: dayjs().add(USER_INVITE_EXPIRY_DAYS, "day"),
        }}
      >
        <Flex vertical style={{ maxWidth: 480, width: "100%" }}>
          <Form.Item
            name="recipient"
            label={t("pages.userInvites.emailAddress")}
            rules={[
              {
                required: true,
                type: "email",
                message: t("pages.login.errors.validEmail"),
              },
            ]}
          >
            <Input
              type="email"
              placeholder={`${t("pages.userInvites.enterRecipientEmail")}\u2026`}
              disabled={!!editingInvite}
              autoComplete="email"
              spellCheck={false}
            />
          </Form.Item>

          <Form.Item
            name="organization_roles"
            label={t("pages.userInvites.organizationRoles")}
          >
            <Select
              mode="multiple"
              placeholder={t("pages.userInvites.selectOrganizationRoles")}
              showSearch
              optionFilterProp="children"
              style={{ width: "100%" }}
              loading={isLoadingRoles}
            >
              {organizationRoleOptions.map((role) => (
                <Select.Option key={role.value} value={role.value}>
                  {role.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="workspace_roles_tree"
            label={t("pages.userInvites.workspaceRoles")}
            style={{ marginBottom: 30 }}
          >
            {workspaces && workspaces.length > 0 ? (
              <Flex
                style={{
                  border: `1px solid ${token.colorBorder}`,
                  borderRadius: radius.box,
                  padding: token.paddingSM,
                  maxHeight: 250,
                  overflowY: "auto",
                }}
              >
                <ConfigProvider
                  theme={{
                    components: {
                      Tree: {
                        colorBgContainer: "transparent",
                      },
                    },
                  }}
                >
                  <Tree
                    key={treeKey}
                    checkable
                    onExpand={onTreeExpand}
                    expandedKeys={workspaceTreeExpandedKeys}
                    autoExpandParent={workspaceTreeAutoExpandParent}
                    onCheck={onTreeCheck}
                    checkedKeys={workspaceTreeCheckedKeys}
                    treeData={workspaceRolesTreeData}
                    showLine={false}
                    showIcon={true}
                    virtual={false}
                    switcherIcon={<DownOutlined />}
                  />
                </ConfigProvider>
              </Flex>
            ) : (
              <Flex
                justify="center"
                align="center"
                style={{
                  padding: token.paddingMD,
                  color: token.colorTextSecondary,
                }}
              >
                {t("pages.userInvites.noWorkspacesAvailable")}
              </Flex>
            )}
          </Form.Item>

          <Flex vertical gap={8}>
            <Form.Item
              label={t("pages.userInvites.expirationDate")}
              style={{ marginBottom: 0 }}
            >
              <Segmented
                options={expiryPresetOptions}
                value={expiryPreset}
                onChange={(value) =>
                  handleExpiryPresetChange(value as ExpiryPreset)
                }
                block
              />
            </Form.Item>
            <Form.Item
              name="expiry"
              rules={[
                {
                  required: true,
                  message: t("pages.userInvites.selectExpiryDate"),
                },
              ]}
              hidden={expiryPreset !== "custom"}
            >
              <DatePicker
                showTime
                placeholder={`${t("pages.userInvites.selectExpiryDate")}\u2026`}
                style={{ width: "100%" }}
                minDate={dayjs()}
                showNow={false}
                disabledTime={(current) => {
                  if (!current || !current.isSame(dayjs(), "day")) return {};

                  const now = dayjs();

                  const createDisabledArray = (maxValue: number) =>
                    Array.from({ length: maxValue }, (_, i) => i);

                  return {
                    disabledHours: () => createDisabledArray(now.hour()),
                    disabledMinutes: (hour: number) =>
                      hour === now.hour()
                        ? createDisabledArray(now.minute() + 1)
                        : [],
                    disabledSeconds: (hour: number, minute: number) =>
                      hour === now.hour() && minute === now.minute()
                        ? createDisabledArray(now.second() + 1)
                        : [],
                  };
                }}
              />
            </Form.Item>
          </Flex>
        </Flex>
        <Space style={{ marginTop: token.marginMD }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={isCreating || isUpdating}
            disabled={isCreating || isUpdating}
            size="middle"
            shape="round"
          >
            {buttonText}
          </Button>
          <Button
            onClick={onCancel}
            size="middle"
            shape="round"
            disabled={isCreating || isUpdating}
          >
            {t("buttons.cancel")}
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
